import { useState } from 'react'
import { Box } from '@mui/material'
import { useFormikContext } from 'formik'
import { t } from 'i18next'
import { useSelector } from 'react-redux'

import EnhancedTable from 'src/components/shared/EnhancedTable/EnhancedTable'
import { fieldsetTableStyles } from 'src/components/shared/styles/FieldsetTableStyles'
import ButtonPrimaryOutlined from 'src/components/shared/ui/ButtonPrimaryOutlined'
import { EditLxIcon } from 'src/components/shared/ui/Icons'
import LexnetDialog from 'src/components/shared/ui/LexnetDialog'
import { SwitchField } from 'src/components/shared/ui/form/fields'
import LexnetIconButton from 'src/components/shared/ui/form/fields/LexnetIconButton'
import RemoveInfoButton from 'src/components/views/EscritosLexnet/components/shared/components/RemoveInfoButton'
import { FormField } from 'src/enums/Form'
import useNewModal from 'src/hooks/useModal'
import { Colegio, TipoRepresentacion } from 'src/interfaces/Catalog'
import { RepresentanteProcesal } from 'src/interfaces/EscritosLegales'
import { userSelector } from 'src/state/authentication/selectors'
import RepresentanteProcesalForm from './RepresentanteProcesalForm'
import { tipoRepresentacionProfesional } from './types'

type selectedRow = {
  index: number
  item: RepresentanteProcesal | null
}

type Profesional = {
  representacionTipo: TipoRepresentacion | null
  codigoColegiado?: string
  codigoConsejo?: string
  colegio?: Colegio | null
  nif?: string
  nombre?: string
  primerApellido?: string
  segundoApellido?: string
  email?: string
  remitente: boolean
}

const profesional: Profesional = {
  representacionTipo: {
    id: 2,
    codigo: 'ABO',
    descripcion: 'Abogado',
    tipo: '1'
  },
  colegio: null,
  primerApellido: '',
  segundoApellido: '',
  nombre: 'Ivan',
  email: '',
  remitente: true
}

function checkProfesional(
  dataTable: RepresentanteProcesal[],
  profesionalData: Profesional
): number {
  return dataTable.findIndex(item => item.nif === profesionalData.nif)
}

export default function RepresentanteProcesalView({
  representantesData = [],
  monitorio = false,
  validateAfterSubmit = false
}: {
  representantesData?: RepresentanteProcesal[]
  monitorio?: boolean
  validateAfterSubmit?: boolean
}): JSX.Element {
  const { isOpened, close, open } = useNewModal()
  const [dataTable, setDataTable] = useState<RepresentanteProcesal[]>(representantesData)
  const [selectedRow, setSelectedRow] = useState<selectedRow>({ index: -1 } as selectedRow)
  const { setFieldValue, errors, validateField } = useFormikContext()
  const user = useSelector(userSelector)

  function addProfesional(value: boolean) {
    const profesionalData: Profesional = {
      ...profesional,
      nombre: user?.name,
      nif: user?.dni,
      codigoColegiado: user?.codigoColegiado,
      colegio: user?.colegio,
      remitente: true
    }
    if (value) {
      setDataTable([...dataTable, profesionalData])
      setFieldValue(
        FormField.INTERVINIENTE_REPRESENTANTES_PROCESALES,
        [...dataTable, profesionalData],
        monitorio && validateAfterSubmit
      )
    } else {
      const index = checkProfesional(dataTable, profesionalData)
      if (index !== -1) {
        dataTable.splice(index, 1)
        setDataTable([...dataTable])
        setFieldValue(
          FormField.INTERVINIENTE_REPRESENTANTES_PROCESALES,
          [...dataTable],
          monitorio && validateAfterSubmit
        )
      }
    }
  }

  function deleteRow(_item: RepresentanteProcesal, index: number) {
    if (index !== -1) {
      const data = [...dataTable.slice(0, index), ...dataTable.slice(index + 1)]
      setDataTable(data)
      setFieldValue(
        FormField.INTERVINIENTE_REPRESENTANTES_PROCESALES,
        data,
        monitorio && validateAfterSubmit
      )
    }
  }

  function editMode() {
    return selectedRow && selectedRow.index !== -1
  }

  function editRow(item: RepresentanteProcesal, index: number) {
    setSelectedRow({ index, item })
    open()
  }

  const columns = [
    {
      Header: 'Tipo de representación',
      accessor: 'representacionTipo.descripcion'
    },
    {
      Header: 'Representante',
      accessor: (item: RepresentanteProcesal) =>
        item.representacionTipo && !tipoRepresentacionProfesional(item.representacionTipo)
          ? item.entidad?.descripcion
          : `${item.nombre} ${item.primerApellido}`
    },
    {
      Header: 'Nº de colegiado',
      accessor: (item: RepresentanteProcesal) =>
        item.representacionTipo && !tipoRepresentacionProfesional(item.representacionTipo)
          ? item.entidad?.codigo
          : item.codigoColegiado
    },
    {
      Header: ' ',
      accessor: (item: RepresentanteProcesal, index: number) => (
        <Box className="cellActions">
          {!item.remitente && (
            <LexnetIconButton
              handleClick={() => editRow(item, index)}
              aria-label={t('tooltips.edit')}
              title={t('tooltips.edit')}
            >
              <EditLxIcon />
            </LexnetIconButton>
          )}
          <RemoveInfoButton
            clear={() => deleteRow(item, index)}
            firstDialogMessage={t('dialog.message.remove_representante_procesal')}
          />
        </Box>
      ),
      width: 100
    }
  ]

  function getRepresentante(values: RepresentanteProcesal) {
    if (editMode()) {
      dataTable[selectedRow.index] = values
      setSelectedRow({ index: -1, item: null })
    }
    const newData = editMode() ? [...dataTable] : [...dataTable, values]
    setDataTable(newData)
    setFieldValue(
      FormField.INTERVINIENTE_REPRESENTANTES_PROCESALES,
      newData,
      monitorio && validateAfterSubmit
    )
  }

  function onCloseDialog() {
    setSelectedRow({ index: -1, item: null })
    close()
  }

  return (
    <>
      <Box sx={fieldsetTableStyles.wrapper}>
        <Box
          sx={fieldsetTableStyles.actions}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Box component="span" flex="1 1 auto" sx={fieldsetTableStyles.actionsInfo}>
            {t('form.legend.representantes_procesales')}
          </Box>
          <ButtonPrimaryOutlined onClick={open}>{t('form.button.add')}</ButtonPrimaryOutlined>
        </Box>
        <EnhancedTable
          variation="form"
          columns={columns}
          data={dataTable && Object.keys(dataTable).length > 0 ? dataTable : []}
          toolbar={false}
          noRegistersMessage={t('components.enhanced_table.no_registers.representante_procesal')}
        />
      </Box>

      {monitorio && errors[FormField.INTERVINIENTE_REPRESENTANTES_PROCESALES] && (
        <Box component="span" sx={fieldsetTableStyles.requiredField}>
          {errors[FormField.INTERVINIENTE_REPRESENTANTES_PROCESALES]}
        </Box>
      )}

      <SwitchField
        id={FormField.INTERVINIENTE_ADD_PROFESIONAL_REPRESENTANTE_PROCESAL}
        label={t('form.field.anadir_representante_procesal_profesional')}
        onChange={addProfesional}
      />
      {monitorio && (
        <SwitchField
          id={FormField.INTERVINIENTE_NO_INTERVIENE_OTRO_PROFESIONAL}
          label={t('form.field.no_interviene_otro_profesional')}
          sx={{ marginTop: '16px' }}
          onChange={() => {
            setTimeout(() => {
              validateField(FormField.INTERVINIENTE_REPRESENTANTES_PROCESALES)
            }, 0)
          }}
        />
      )}
      <LexnetDialog
        title={
          editMode()
            ? t('form.dialog.title.editar_representante_procesal')
            : t('form.dialog.title.nuevo_representante_procesal')
        }
        isOpen={isOpened}
        close={onCloseDialog}
      >
        <RepresentanteProcesalForm
          closeDialog={onCloseDialog}
          getFormValues={getRepresentante}
          formValues={selectedRow.item}
        />
      </LexnetDialog>
    </>
  )
}
